import { useEffect, useState } from 'react';

/**
 * Client-only browser state, each written the same way: a value that is *false or empty*
 * during SSR and on the first client render, then real after the first effect.
 *
 * That shape is not laziness — §1.10 forbids module-scope `window`/`document` access in
 * the public tree, and React would log a hydration mismatch for any hook whose first
 * client render disagreed with the server's HTML. Starting from the server's answer and
 * correcting in an effect is the only version of these that is both SSR-safe and honest.
 */

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const list = window.matchMedia(query);
    setMatches(list.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * `prefers-reduced-motion`. Components that animate meaning — the ticker, the hero
 * rotator — read this and present a static alternative; purely decorative motion is
 * handled globally by the CSS rule in `styles.css` and needs no hook.
 */
export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

export function useIsMobile(): boolean {
  // Matches the `md` breakpoint in `tailwind.config.ts`, where the rail becomes a drawer.
  return useMediaQuery('(max-width: 719px)');
}

/** `false` only once the browser says so — an SSR render must never claim to be offline. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * The ticker's clock (§1.12: "the ticker clock is client-side"). Returns an empty string
 * on the server and on the first client render, so the server's HTML and the hydrated
 * output agree — a clock rendered server-side would be wrong by exactly the time the
 * response spent in a CDN, which is the one place it must not be.
 */
export function useClock(locale: string, timeZone = 'Asia/Kolkata'): string {
  const [time, setTime] = useState('');

  useEffect(() => {
    const format = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
    const tick = () => setTime(format.format(new Date()));
    tick();
    const timer = setInterval(tick, 30_000);
    return () => clearInterval(timer);
  }, [locale, timeZone]);

  return time;
}

/** Vertical scroll progress, 0–1. Used by the reading-progress bar and back-to-top. */
export function useScrollProgress(): { progress: number; scrolled: number } {
  const [state, setState] = useState({ progress: 0, scrolled: 0 });

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      setState({ progress: max > 0 ? Math.min(1, scrolled / max) : 0, scrolled });
    };
    // Scroll fires far more often than the screen repaints; coalesce to one frame.
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return state;
}

/**
 * A clock for relative timestamps — "4 minutes ago" has to become "5 minutes ago"
 * without a reload (Phase 11).
 *
 * `0` until the component has mounted, which is deliberate: a relative time rendered on
 * the server is computed against the server's clock and the moment of the *render*, and
 * would disagree with the browser a second later. Callers show an absolute time until
 * this returns a real value, so the server's HTML and the hydrated output always match.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(0);

  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return now;
}

/**
 * True once the page has finished loading and the browser has gone idle — Phase 17.
 *
 * The breaking-news stream is the reason this exists. `EventSource` opens at **high**
 * priority and never completes, so a connection opened during hydration competes with
 * the fonts and the hero image for the first second of a slow connection, and holds one
 * of the browser's connections for as long as the reader stays. Deferring it costs
 * nothing that matters: the ticker's contents are server-rendered (§1.10), so the reader
 * is already looking at the current headlines while the stream is still connecting.
 *
 * `requestIdleCallback` where it exists, a timeout where it does not (Safari, until
 * recently). Either way the wait is bounded, so a busy page still connects.
 */
export function useAfterLoad(timeoutMs = 2_000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    const done = () => {
      if (!cancelled) setReady(true);
    };

    const idle = () => {
      const schedule = window.requestIdleCallback;
      if (typeof schedule === 'function') {
        const handle = schedule(done, { timeout: timeoutMs });
        return () => window.cancelIdleCallback?.(handle);
      }
      const handle = setTimeout(done, 200);
      return () => clearTimeout(handle);
    };

    // `load` has usually already fired by the time React hydrates, which is the common
    // case and the one that must not wait for an event that will never come again.
    if (document.readyState === 'complete') return idle();

    let cancelIdle: (() => void) | undefined;
    const onLoad = () => {
      cancelIdle = idle();
    };
    window.addEventListener('load', onLoad, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener('load', onLoad);
      cancelIdle?.();
    };
  }, [timeoutMs]);

  return ready;
}
